if (!customElements.get('astari-announcement')) {
  customElements.define('astari-announcement', class extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'))
      this.index = 0
      this.animations = []
      this.transitioning = false
      this.pendingIndex = null
      this.paused = false
      this.hovered = false
      this.focused = false
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)')
      this.abort = new AbortController()
      const options = { signal: this.abort.signal }
      this.previous = this.querySelector('[data-announcement-previous]')
      this.next = this.querySelector('[data-announcement-next]')
      this.pause = this.querySelector('[data-announcement-pause]')
      this.controls = this.querySelector('.astari-announcement__controls')
      if (this.controls) this.controls.hidden = false
      this.previous?.addEventListener('click', () => this.show(this.index - 1), options)
      this.next?.addEventListener('click', () => this.show(this.index + 1), options)
      this.pause?.addEventListener('click', () => {
        this.paused = !this.paused
        this.updatePause()
        this.schedule()
      }, options)
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return
        this.hovered = true
        this.schedule()
      }, options)
      this.addEventListener('pointerleave', () => {
        this.hovered = false
        this.schedule()
      }, options)
      this.addEventListener('focusin', () => {
        this.focused = true
        this.schedule()
      }, options)
      this.addEventListener('focusout', (event) => {
        this.focused = this.contains(event.relatedTarget)
        this.schedule()
      }, options)
      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch' || event.target.closest('button')) return
        this.touchStart = { x: event.clientX, y: event.clientY }
      }, options)
      this.addEventListener('pointerup', (event) => {
        if (!this.touchStart) return
        const dx = event.clientX - this.touchStart.x
        const dy = event.clientY - this.touchStart.y
        this.touchStart = null
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.suppressClick = true
          this.show(this.index + (dx < 0 ? 1 : -1))
          window.clearTimeout(this.clickTimer)
          this.clickTimer = window.setTimeout(() => { this.suppressClick = false }, 400)
        }
      }, options)
      this.addEventListener('pointercancel', () => { this.touchStart = null }, options)
      this.addEventListener('click', (event) => {
        if (this.suppressClick) {
          event.preventDefault()
          this.suppressClick = false
        }
      }, { ...options, capture: true })
      document.addEventListener('visibilitychange', () => this.schedule(), options)
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.animations.forEach((animation) => animation.finish())
        this.updatePause()
        this.schedule()
      }, options)
      this.show(0)
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer)
      window.clearTimeout(this.clickTimer)
      this.pendingIndex = null
      this.transitioning = false
      this.animations.forEach((animation) => animation.cancel())
      this.slides.forEach((slide) => slide.classList.remove('is-leaving'))
      this.abort?.abort()
    }

    updatePause() {
      if (!this.pause) return
      const stopped = this.paused || this.motion.matches
      this.pause.disabled = this.motion.matches
      this.pause.setAttribute('aria-label', stopped ? this.pause.dataset.playLabel : this.pause.dataset.pauseLabel)
      this.pause.textContent = stopped ? this.pause.dataset.playLabel : this.pause.dataset.pauseLabel
    }

    show(index) {
      if (!this.slides.length) return
      if (this.transitioning) {
        this.pendingIndex = index
        return
      }
      const previous = this.slides[this.index]
      const direction = index >= this.index ? 1 : -1
      this.animations.forEach((animation) => animation.cancel())
      this.animations = []
      this.index = (index + this.slides.length) % this.slides.length
      this.slides.forEach((slide, i) => {
        const active = i === this.index
        slide.classList.remove('is-leaving')
        slide.classList.toggle('is-active', active)
        slide.setAttribute('aria-hidden', String(!active))
        slide.inert = !active
      })
      const active = this.slides[this.index]
      if (previous !== active && !this.motion.matches && this.dataset.animation !== 'none') {
        const fade = this.dataset.animation === 'fade'
        const distance = 100 * direction
        const options = { duration: 1300, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'both' }
        this.transitioning = true
        previous.classList.add('is-leaving')
        this.animations = [
          previous.animate([
            { opacity: 1, transform: 'translateX(0)' },
            { opacity: fade ? 0 : 1, transform: fade ? 'translateX(0)' : `translateX(${distance}%)` },
          ], options),
          active.animate([
            { opacity: fade ? 0 : 1, transform: fade ? 'translateX(0)' : `translateX(${-distance}%)` },
            { opacity: 1, transform: 'translateX(0)' },
          ], options),
        ]
        Promise.all(this.animations.map((animation) => animation.finished)).then(() => {
          previous.classList.remove('is-leaving')
          this.animations.forEach((animation) => animation.cancel())
          this.animations = []
          this.transitioning = false
          const pending = this.pendingIndex
          this.pendingIndex = null
          if (pending !== null) this.show(pending)
        }).catch(() => {})
      }
      this.updatePause()
      this.schedule()
    }

    schedule() {
      window.clearTimeout(this.timer)
      if (this.slides.length < 2 || this.dataset.autoRotate !== 'true' ||
          this.paused || this.hovered || this.focused || this.motion.matches ||
          document.hidden || window.Shopify?.designMode) return
      const interval = Math.max(3000, Number(this.dataset.interval) || 5000)
      this.timer = window.setTimeout(() => this.show(this.index + 1), interval)
    }
  })
}
